import { useState, useRef, useEffect } from 'react'
import { Sun, Moon, Monitor, ChevronDown, Check, type LucideIcon } from 'lucide-react'
import { useTheme, type Theme } from '../../contexts/ThemeContext'

interface ThemeToggleProps {
  /** Display variant */
  variant?: 'icon' | 'dropdown' | 'full'
  /** Additional CSS classes */
  className?: string
}

const themeOptions: { value: Theme; label: string; icon: LucideIcon }[] = [
  { value: 'system', label: 'System Default', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
]

/**
 * Theme toggle component with multiple display variants
 * Theme preferences are available for all users (free feature)
 *
 * - `icon`: Simple icon button that toggles between themes
 * - `dropdown`: Button with dropdown menu to select theme
 * - `full`: Full display showing current theme with dropdown
 */
export function ThemeToggle({ variant = 'dropdown', className = '' }: ThemeToggleProps) {
  const { theme, setTheme, toggleTheme, isDark } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdown on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const currentThemeOption = themeOptions.find((opt) => opt.value === theme) || themeOptions[0]
  const CurrentIcon = isDark ? Moon : Sun

  const handleThemeSelect = (value: Theme) => {
    setTheme(value)
    setIsOpen(false)
  }

  // Icon variant - simple toggle button
  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${className}`}
        title={`Current: ${currentThemeOption.label}. Click to switch.`}
        aria-label={`Current theme: ${currentThemeOption.label}. Click to switch.`}
      >
        <CurrentIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
      </button>
    )
  }

  // Dropdown and full variants
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 rounded-lg transition-colors ${
          variant === 'full'
            ? 'w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            : 'inline-flex h-11 w-11 items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800'
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select theme"
      >
        <CurrentIcon
          className={`h-5 w-5 ${
            variant === 'full'
              ? 'text-gray-700 dark:text-gray-200'
              : 'text-gray-600 dark:text-gray-300'
          }`}
        />
        {variant === 'full' && (
          <>
            <span className="flex-1 text-left text-sm font-medium text-gray-700 dark:text-gray-200">
              {currentThemeOption.label}
            </span>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-1 w-52 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 py-1 animate-fade-in"
          role="listbox"
          aria-label="Theme options"
        >
          {themeOptions.map((option) => {
            const Icon = option.icon
            const isSelected = theme === option.value

            return (
              <button
                key={option.value}
                onClick={() => handleThemeSelect(option.value)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  isSelected
                    ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                role="option"
                aria-selected={isSelected}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{option.label}</span>
                {isSelected && <Check className="h-4 w-4" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Simple theme toggle button for compact spaces
 * Toggles between light and dark
 */
export function ThemeToggleButton({ className = '' }: { className?: string }) {
  return <ThemeToggle variant="icon" className={className} />
}

/**
 * Theme selector dropdown for navigation bars
 */
export function ThemeDropdown({ className = '' }: { className?: string }) {
  return <ThemeToggle variant="dropdown" className={className} />
}

/**
 * Full theme selector for settings pages
 */
export function ThemeSelector({ className = '' }: { className?: string }) {
  return <ThemeToggle variant="full" className={className} />
}
